import React, { FC, useState } from 'react';
import styled from '@emotion/styled';
import {
    Image,
    Flag,
    Tag,
    Icon,
    Text,
    IconAndText,
    ButtonWithIcon
} from '@blueprint/ui';
import { Props, SubImage } from './ListingCard.types';
import { ResponsiveImage } from '../Listings/Listings.types';
import placeHolderImage from 'public/assets/place-holder-image.jpg';

import {
    TransportWrapper,
    StyledWrapper,
    ImageWrapper,
    DateDetailWrapper,
    ViewCountContainer,
    StyledContent,
    JustAddedFlag,
    StyledHelpToBuy,
    WrapperFeatures,
    StyledAddress,
    CardFooter,
    CardFooterCell,
    Wrapper,
    CardHeader,
    PriceContainer,
    AgencyLogoLink,
    MediaLinksContainer,
    ImageContainer,
    ImageCenterWrapper,
    SubImageContainer,
    SubImageWrapper,
    FullCardLink,
    ImageLink,
    CustomStyledImage,
    AvailableFromContainer,
    StyledTag,
    PriceDropText,
    PriceTitleText,
    ContactLink
} from './ListingCard.styles';

import { useRedirectWithSearchIndentifier } from './hooks/useRedirectWithSearchIndentifier';

const DESKTOP_IMAGE_INDEX = 1;

const SUB_IMAGES_NUMBER = 4;

const filterEmptyResponsiveImages = (
    responsiveImages: ResponsiveImage[] | undefined
) => {
    if (!responsiveImages) {
        return [];
    }

    const [first, second] = responsiveImages;

    const firstDoesntExist = first.src === null;
    const secondDoesntExist = second.src === null;

    if (firstDoesntExist && secondDoesntExist) {
        return undefined;
    }

    return responsiveImages;
};

const DEFAULT_IMAGE_ALT_TEXT = 'Thumbnail';
const getImageAltText = (props: Props) => {
    const { title, address } = props;

    if (title && address) {
        return `${title} in ${address}`;
    }

    return DEFAULT_IMAGE_ALT_TEXT;
};

const MOBILE_IMAGE_INITIAL_WIDTH = 480;
const DESKTOP_IMAGE_INITIAL_WIDTH = 960;
// Aspect ratio of the listing card image is 43:31
const MOBILE_IMAGE_INITIAL_HEIGHT = MOBILE_IMAGE_INITIAL_WIDTH * (31 / 43);

// Aspect ratio of the thumbnail image is 69:50
const THUMBNAIL_IMAGE_INITIAL_WIDTH = 69;
const THUMBNAIL_IMAGE_INITIAL_HEIGHT =
    THUMBNAIL_IMAGE_INITIAL_WIDTH * (50 / 69);

// Aspect ratio of the agency logo image is 2:1
const AGENCY_LOGO_IMAGE_INITIAL_WIDTH = 73;
const AGENCY_LOGO_IMAGE_INITIAL_HEIGHT =
    AGENCY_LOGO_IMAGE_INITIAL_WIDTH * (1 / 2);

const ListingCardComponent: FC<Props> = (props) => {
    const {
        listingId,
        numberOfVideos,
        numberOfImages,
        numberOfFloorPlans,
        flag,
        isHelptoBuy,
        isPremium,
        underOffer = false,
        priceDrop,
        otherPropertyImages,
        priceTitle,
        price,
        branch,
        features,
        title,
        address,
        transports,
        highlights,
        tags,
        publishedOn,
        publishedOnLabel,
        numberOfViews,
        viewsLabel,
        viewsPeriodLabel,
        saveListing,
        contactOnClick,
        phoneOnClick,
        agentLogoOnClick,
        imageOnClick,
        cardOnClick,
        isFavourite,
        removeFavouriteLabel,
        saveFavouriteLabel,
        listingUris,
        image,
        searchIdentifier,
        availableFrom,
        availableFromLabel,
        'data-testid': dataTestId,
        searchType = 'for-sale',
        overrideViewsWithAvailability = false,
        useLazyImage = true,
        className
    } = props;

    const imageAlt = getImageAltText(props);
    const responsiveImages = filterEmptyResponsiveImages(
        image?.responsiveImgList
    );

    const defaultImage = responsiveImages
        ? responsiveImages[DESKTOP_IMAGE_INDEX].src
        : placeHolderImage;

    const [listingsImage, setListingsImage] = useState(defaultImage);

    const [responsiveImgList, setResponsiveImgList] = useState(
        responsiveImages
    );
    const [isImageLazy, setImageLazy] = useState(useLazyImage);

    const resetToDefaultImage = () => {
        setListingsImage(defaultImage);
        setResponsiveImgList(responsiveImages);
    };

    const swapMainImage = (item: SubImage) => {
        setListingsImage(item.large);
        setResponsiveImgList([
            {
                width: MOBILE_IMAGE_INITIAL_WIDTH,
                src: item.small
            },
            {
                width: DESKTOP_IMAGE_INITIAL_WIDTH,
                src: item.large
            }
        ]);
        setImageLazy(false);
    };

    const detailsLink = listingUris?.detail ? `${listingUris?.detail}` : '#';

    const availableFeatures = features.filter((feature) => feature.content > 0);
    const premiumImages =
        isPremium && otherPropertyImages
            ? otherPropertyImages.slice(0, SUB_IMAGES_NUMBER)
            : [];
    const contactLink = listingUris?.contact?.replace(
        'contact',
        'details/contact'
    );

    const redirectToListing = useRedirectWithSearchIndentifier({
        searchIdentifier,
        listingUris,
        listingId
    });

    return (
        <Wrapper data-testid={dataTestId} className={className}>
            <StyledWrapper>
                <ImageWrapper>
                    <ImageContainer>
                        {!!listingsImage && (
                            <ImageCenterWrapper>
                                <ImageLink
                                    data-testid="listing-details-image-link"
                                    href={detailsLink}
                                    onClick={(event) => {
                                        event.preventDefault();
                                        imageOnClick && imageOnClick();
                                        redirectToListing();
                                    }}
                                >
                                    <CustomStyledImage
                                        data-testid="listing-image"
                                        src={listingsImage}
                                        lazy={isImageLazy}
                                        responsiveImgList={responsiveImgList}
                                        alt={imageAlt}
                                        height={MOBILE_IMAGE_INITIAL_HEIGHT}
                                        width={MOBILE_IMAGE_INITIAL_WIDTH}
                                    />
                                </ImageLink>
                            </ImageCenterWrapper>
                        )}
                        <MediaLinksContainer>
                            {numberOfVideos > 0 && (
                                <Tag>
                                    <Icon
                                        iconId="play"
                                        color="white"
                                        size="small"
                                    />
                                    {numberOfVideos}
                                </Tag>
                            )}
                            {numberOfImages > 0 && (
                                <Tag>
                                    <Icon
                                        iconId="camera"
                                        color="white"
                                        size="small"
                                    />
                                    {numberOfImages}
                                </Tag>
                            )}
                            {numberOfFloorPlans > 0 && (
                                <Tag>
                                    <Icon
                                        iconId="floorplan"
                                        color="white"
                                        size="small"
                                    />
                                    {numberOfFloorPlans}
                                </Tag>
                            )}
                        </MediaLinksContainer>
                        {flag && (
                            <JustAddedFlag variant="justAdded">
                                {flag}
                            </JustAddedFlag>
                        )}
                        {isHelptoBuy && <StyledHelpToBuy />}
                    </ImageContainer>
                    {premiumImages.length > 0 && (
                        <SubImageWrapper data-testid="premium-listing-images">
                            {premiumImages.map((item, index) => {
                                const { caption, small } = item;
                                const title = caption !== null ? caption : '';
                                return (
                                    <SubImageContainer
                                        data-testid={`listing-subimage-${index}`}
                                        onClick={() => swapMainImage(item)}
                                        onMouseOver={() => swapMainImage(item)}
                                        onMouseOut={() => resetToDefaultImage()}
                                        key={`${address}-${index}`}
                                        src={small}
                                        width={THUMBNAIL_IMAGE_INITIAL_WIDTH}
                                        height={THUMBNAIL_IMAGE_INITIAL_HEIGHT}
                                        title={title}
                                        alt={`${address}. Sub image ${index}. ${title}`}
                                    />
                                );
                            })}
                        </SubImageWrapper>
                    )}
                </ImageWrapper>
                <StyledContent>
                    <CardHeader>
                        <PriceContainer>
                            {isPremium && (
                                <Flag
                                    data-testid="premium-listing"
                                    variant="premium"
                                >
                                    Premium listing
                                </Flag>
                            )}
                            {underOffer && (
                                <Flag
                                    data-testid="under-offer"
                                    variant="underOffer"
                                >
                                    Under offer
                                </Flag>
                            )}
                            {priceTitle && (
                                <PriceTitleText size="secondary">
                                    {priceTitle}
                                </PriceTitleText>
                            )}
                            <Text size="6" isBold>
                                {price}
                            </Text>
                            {priceDrop && (
                                <PriceDropText
                                    size="secondary"
                                    color="grey-slate"
                                    data-testid="price-drop-label"
                                >
                                    <Icon
                                        iconId="arrow_full_down"
                                        color="crimson"
                                        size="medium"
                                    />{' '}
                                    <strong>
                                        {priceDrop.percentageChangeLabel.replace(
                                            '.0',
                                            ''
                                        )}
                                        %
                                    </strong>{' '}
                                    - {priceDrop.lastPriceChangeDate}
                                </PriceDropText>
                            )}
                        </PriceContainer>

                        <AgencyLogoLink
                            plain
                            href={branch.branchDetailsUri}
                            data-testid="listing-details-agent-logo"
                            onClick={agentLogoOnClick}
                        >
                            {branch.logoUrl === 'false' ? (
                                <Text data-testid="listing-details-agent-name">
                                    Marketed by {branch.name}
                                </Text>
                            ) : (
                                <Image
                                    src={branch.logoUrl}
                                    alt={`Marketed by ${branch.name}`}
                                    height={AGENCY_LOGO_IMAGE_INITIAL_HEIGHT}
                                    width={AGENCY_LOGO_IMAGE_INITIAL_WIDTH}
                                />
                            )}
                        </AgencyLogoLink>
                    </CardHeader>
                    <WrapperFeatures>
                        {availableFeatures.map((item, index) => {
                            const { iconId, content } = item;

                            return (
                                <IconAndText
                                    isInline
                                    key={`${index}-${iconId}`}
                                    iconId={iconId}
                                    content={content}
                                    iconColor={'grey-steel'}
                                    contentColor={'grey-slate'}
                                    size="medium"
                                />
                            );
                        })}
                    </WrapperFeatures>
                    <FullCardLink
                        plain
                        data-testid="listing-details-link"
                        href={detailsLink}
                        onClick={(event) => {
                            event.preventDefault();
                            cardOnClick && cardOnClick();
                            redirectToListing();
                        }}
                    >
                        {title && (
                            <StyledAddress size="8" color={'text-title'}>
                                {title}
                            </StyledAddress>
                        )}
                        <Text size="secondary" color={'grey-steel'}>
                            {address}
                        </Text>
                    </FullCardLink>
                    <TransportWrapper>
                        {transports.map((item, i: number) => (
                            <IconAndText
                                isInline
                                key={i}
                                iconId={item.poiType}
                                content={`${item.distanceInMiles} miles ${item.title}`}
                                contentColor="grey-steel"
                                size="small"
                            />
                        ))}
                        {isPremium &&
                            highlights &&
                            highlights.map((item, i) => (
                                <IconAndText
                                    data-testid="premium-plus-feature"
                                    isInline
                                    key={i}
                                    iconId="tick"
                                    content={item.label}
                                    iconColor="primary-brand"
                                    contentColor="grey-steel"
                                    size="small"
                                />
                            ))}
                    </TransportWrapper>
                    {tags &&
                        tags.map((item, i) => (
                            <StyledTag key={i}>{item.content}</StyledTag>
                        ))}
                </StyledContent>
            </StyledWrapper>
            <CardFooter>
                <DateDetailWrapper>
                    {publishedOn && (
                        <Text
                            as="span"
                            size="small"
                            color={'grey-steel'}
                            data-testid="date-published"
                        >
                            {publishedOnLabel} {publishedOn}
                        </Text>
                    )}
                    {numberOfViews > 0 && !overrideViewsWithAvailability && (
                        <ViewCountContainer>
                            <Text
                                as="span"
                                size="small"
                                isBold
                                color={'grey-steel'}
                                data-testid="number-of-views"
                            >
                                {`${numberOfViews} ${viewsLabel}`}
                            </Text>
                            <Text
                                as="span"
                                size="small"
                                color={'grey-steel'}
                                data-testid="number-of-views-label"
                            >
                                {`\u00A0${viewsPeriodLabel}`}
                            </Text>
                        </ViewCountContainer>
                    )}
                    {((numberOfViews === 0 &&
                        searchType === 'to-rent' &&
                        availableFrom) ||
                        overrideViewsWithAvailability) && (
                        <AvailableFromContainer>
                            <Text
                                as="span"
                                size="small"
                                color={'grey-steel'}
                                data-testid="available-from-label"
                            >
                                {availableFromLabel}
                            </Text>
                            <Text
                                as="span"
                                size="small"
                                isBold
                                color={'grey-steel'}
                                data-testid="available-from-date"
                            >
                                {`\u00A0${availableFrom}`}
                            </Text>
                        </AvailableFromContainer>
                    )}
                </DateDetailWrapper>

                <CardFooterCell>
                    {!!listingUris && (
                        <>
                            {!!branch?.phone && (
                                <ContactLink
                                    href={`tel:${branch.phone}`}
                                    onClick={phoneOnClick}
                                    variant="tertiary"
                                    data-testid="agent-phone-number"
                                >
                                    <Icon
                                        iconId="phone_outline"
                                        size="medium"
                                    />
                                    {branch.phone}
                                </ContactLink>
                            )}

                            {!!contactLink && (
                                <ContactLink
                                    href={contactLink}
                                    onClick={contactOnClick}
                                    variant="tertiary"
                                    data-testid="agent-contact-link"
                                >
                                    <Icon
                                        iconId="email_outline"
                                        size="medium"
                                    />
                                    Contact
                                </ContactLink>
                            )}
                        </>
                    )}
                    <ButtonWithIcon
                        variant="tertiary"
                        data-testid="save-listing"
                        onClick={saveListing}
                        aria-label={
                            isFavourite
                                ? removeFavouriteLabel
                                : saveFavouriteLabel
                        }
                    >
                        <Icon
                            iconId={
                                isFavourite ? 'save_filled' : 'save_outline'
                            }
                            size="medium"
                        />{' '}
                        {isFavourite ? 'Saved' : 'Save'}
                    </ButtonWithIcon>
                </CardFooterCell>
            </CardFooter>
        </Wrapper>
    );
};

/**
| **Team**               |       |
| -----------------------|--------------|
| **Zeroheight**         |   https://zeroheight.com/8f6f2b33a/v/latest/p/07d92d-listing-cards/b/346990   |
**/
const ListingCard = styled(ListingCardComponent)();
export default ListingCard;
